/**
 * SignInEmbedPage — the chromeless customer sign-in embed route.
 *
 * The `www` marketing site's "Sign In" CTA opens the same hardened modal
 * shell as the SOW sign-up (nexa-website sow-modal.js) with THIS route in the
 * cross-origin <iframe> — the visitor signs in without leaving the site, and
 * on success the shell navigates the top window into the portal (the Amplify
 * session lives on the app origin, so the portal loads authenticated).
 *
 * Hardening mirrors /embed/sow (ADR-080 Amendment 2 §A2.3):
 *  - `frame-ancestors` CSP on this route is locked to the `www` origin(s)
 *    only — see portal-subscriber/public/_headers.
 *  - postMessage is origin-validated BOTH sides via sow-embed-bridge /
 *    usePostToShell; the schema is explicit (hello/init handshake, then
 *    resize/close/success). No credentials or tokens ever cross the boundary
 *    — `success` is a bare signal; the session stays on the app origin.
 *  - A top-level (unframed) visit is redirected to the marketing site — the
 *    embed is not a navigable page. The real sign-in page remains /sign-in.
 *
 * Auth edge cases inside the frame (ADR-092 D5 — the escape hatches are bridge
 * actions, never `target="_blank"` links to the top-level auth pages, which was
 * the Dashlane silent-autofill vector):
 *  - NEW_PASSWORD_REQUIRED (staff-created account with a temp password): the
 *    challenge lives in Amplify's in-memory state, which cannot survive a
 *    top-window navigation — so the embed shows a notice with a "Continue in the
 *    portal" action that asks the shell to navigate the top window to /sign-in.
 *  - Forgot password: asks the shell to navigate the top window to the portal's
 *    /forgot-password (the reset flow is a multi-step page journey, not a
 *    modal-sized dialog) via the same validated nexa-sow:navigate-top message.
 *
 * ⛔ BOT CHECK — ENFORCED (2 October 2026). Same contract as SignInPage: the `nexa-signup` widget
 * renders inside the frame (its hostname is the frame's, `app.nexanomics.com`, which is on the
 * widget), Sign in waits for a token, and the token rides in `clientMetadata.turnstileToken` to
 * the subscriber pool's PreAuthentication trigger, which refuses without a verified one.
 */
import { useEffect, useRef, useState } from 'react';
import { signInFresh } from '../lib/fresh-sign-in';
import { PasswordInput } from '../components/PasswordInput';
import { usePostToShell, MARKETING_SITE_URL } from '../lib/use-post-to-shell';
import { useTurnstile } from '../lib/turnstile';
import { signInErrorMessage, isPreAuthRefusal } from '../lib/sign-in-errors';
import { ENV } from '../amplify-config';
// ADR-104 Layer 1 — `submit-error` from the SIGN-IN embed too ("both embed
// forms"), and `turnstile-error` since the route gained its bot check (2 October
// 2026). It reads no availability gate, so that phase has no call site here.
import { beaconEmbedFailure } from '../lib/embed-health-beacon';

/** A borderless link-style button — the escape hatches are bridge actions now
 *  (ADR-092 D5: no `target="_blank"` portal links inside the embed), not <a>. */
const linkBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--accent)',
  cursor: 'pointer',
  padding: 0,
  font: 'inherit',
};

export function SignInEmbedPage() {
  const { post, framed } = usePostToShell();
  const contentRef = useRef<HTMLDivElement>(null);

  // §A2.2 — the embed is an invisible sandbox, not a navigable page. An
  // unframed (top-level) production visit is sent to the marketing site.
  // Dev keeps top-level access for local work.
  useEffect(() => {
    if (!framed && !import.meta.env.DEV) {
      window.location.replace(MARKETING_SITE_URL);
    }
  }, [framed]);

  // Auto-resize (§A2.3) — observe the content's natural height and report it
  // to the shell so the framed dialog reads as one continuous modal.
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        post({ type: 'nexa-sow:resize', height: Math.ceil(el.scrollHeight) });
      });
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [post]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 'form' → the credentials dialog; 'done' → signed in, shell is navigating;
  // 'new-password' → temp-password account, continue in the portal.
  const [phase, setPhase] = useState<'form' | 'done' | 'new-password'>('form');
  const turnstile = useTurnstile(ENV.TURNSTILE_SITE_KEY_PORTAL, 'dark');

  // A widget that cannot load leaves a form nobody can submit, and the synthetic
  // probe (which never submits) cannot see that — report it, as the SOW embed does.
  useEffect(() => {
    if (turnstile.loadError) void beaconEmbedFailure('signin', 'turnstile-error');
  }, [turnstile.loadError]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!turnstile.token) {
      setError('Please complete the security check.');
      return;
    }
    setBusy(true);
    try {
      // Clean-boundary sign-in: discard any lingering session first so the embed
      // never authenticates on top of a prior user's tokens (tenant-isolation
      // context-leak fix). See lib/fresh-sign-in.ts.
      const result = await signInFresh({
        username: email,
        password,
        options: { clientMetadata: { turnstileToken: turnstile.token } },
      });
      if (result.nextStep.signInStep === 'DONE') {
        // Session established on the app origin. Tell the shell — it
        // navigates the top window into the portal. The success message is a
        // bare signal (no tokens cross the boundary).
        setPhase('done');
        post({ type: 'nexa-sow:success' });
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // The in-memory challenge can't survive the shell's top-level
        // navigation — hand over to the portal's own sign-in page instead.
        setPhase('new-password');
      } else {
        setError('An unexpected sign-in step occurred. Please contact support.');
        // A Cognito step this embed has no branch for — a pool-configuration
        // change, not a visitor mistake. Nobody can sign in through the modal
        // until it is handled, and the synthetic probe cannot see it (the probe
        // never submits credentials, it only asserts the form renders).
        void beaconEmbedFailure('signin', 'submit-error');
      }
    } catch (err: unknown) {
      const ex = err as { name?: string; message?: string };
      setError(signInErrorMessage(err));
      // The token was spent by this attempt (single-use): clear it and re-challenge.
      turnstile.reset();
      if (ex.name === 'NotAuthorizedException' || ex.name === 'UserNotFoundException' || isPreAuthRefusal(err)) {
        // ⚠ NOT REPORTED, and this is the same judgement as excluding 409 on the
        // sign-up form. A wrong password is the auth system WORKING; it is also
        // the single most common outcome of any sign-in form. Counting it would
        // hold this alert permanently open on healthy traffic — and an alert that
        // is always on is an alert nobody reads. A pre-auth refusal (bot check,
        // expired temp password) is the same: the trigger working as designed.
      } else {
        // Everything else: a network failure, a Cognito outage, a throttle, a
        // misconfigured pool. Real, and invisible everywhere else — Cognito
        // errors are not Lambda errors, so Layer 2's alarms never see them.
        void beaconEmbedFailure('signin', 'submit-error');
      }
    } finally {
      setBusy(false);
    }
  }

  // Shared chromeless card — height observed for the shell's auto-resize.
  function frame(children: React.ReactNode) {
    return (
      <main className="sow-embed-page">
        <div className="sow-embed-content" ref={contentRef}>
          <div className="auth-card">
            <div className="auth-logo-wrap">
              <img src="/nexa-logo.png" alt="Nexanomics" className="auth-logo" />
            </div>
            {children}
          </div>
        </div>
      </main>
    );
  }

  if (phase === 'done') {
    return frame(
      <>
        <h1 className="auth-title">Signed in</h1>
        <p className="auth-description" aria-live="polite">
          Taking you to your portal…
        </p>
        <p className="auth-description" style={{ fontSize: '14px', color: 'var(--text-dim)' }}>
          Not redirected?{' '}
          <button type="button" style={linkBtnStyle} onClick={() => post({ type: 'nexa-sow:navigate-top', to: 'portal' })}>
            Open your portal
          </button>.
        </p>
      </>,
    );
  }

  if (phase === 'new-password') {
    return frame(
      <>
        <h1 className="auth-title">Set a new password</h1>
        <p className="auth-description">
          Your account is using a temporary password. Continue in the portal to set a new one and
          finish signing in.
        </p>
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%', marginTop: '12px' }}
          onClick={() => post({ type: 'nexa-sow:navigate-top', to: 'sign-in' })}
        >
          Continue in the portal
        </button>
      </>,
    );
  }

  return frame(
    <>
      <h1 className="auth-title">Customer sign-in</h1>

      {error && (
        <div className="auth-error" role="alert">{error}</div>
      )}

      <form onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div className="form-field">
          <label htmlFor="embed-signin-email">Email address</label>
          <input
            id="embed-signin-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={busy}
          />
        </div>
        <div className="form-field">
          <label htmlFor="embed-signin-password">Password</label>
          <PasswordInput
            id="embed-signin-password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={busy}
          />
        </div>
        <div className="form-forgot">
          {/* The reset flow is a full page journey — the shell navigates the top
              window there (ADR-092 D5: no target="_blank" portal links in-frame). */}
          <button
            type="button"
            style={linkBtnStyle}
            onClick={() => post({ type: 'nexa-sow:navigate-top', to: 'forgot-password' })}
          >
            Forgot password?
          </button>
        </div>
        <div ref={turnstile.containerRef} className="portal-turnstile" aria-label="Security check" data-testid="signin-turnstile" />
        {turnstile.loadError && (
          <div className="auth-error" role="alert">
            The security check could not load. Refresh the page to try again.
          </div>
        )}
        <button type="submit" className="btn-primary" disabled={busy || !email || !password || !turnstile.token}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </>,
  );
}
